import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  Chip,
  Divider,
  CircularProgress,
  Checkbox,
  ListItemIcon,
  TextField,
  Tab,
  Tabs,
  Pagination,
  InputAdornment,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import { 
  getPavoPaySales, 
  cancelPavoPaySale, 
  removePavoPaySale,
  PavoPaySaleRecord 
} from '../../hooks/fiscalDevices/pavopay';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pavopay-tabpanel-${index}`}
      aria-labelledby={`pavopay-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

interface PavoPayRefundModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  showAlertModal: (title: string, message: string) => void;
  createdBy?: string;
}

const PavoPayRefundModal: React.FC<PavoPayRefundModalProps> = ({
  open,
  onClose,
  companyId,
  showAlertModal,
  createdBy,
}) => {
  const { t } = useTranslation('salesSystem');
  const [tabValue, setTabValue] = useState(0);
  const [sales, setSales] = useState<PavoPaySaleRecord[]>([]);
  const [selectedSaleNumbers, setSelectedSaleNumbers] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [refundResults, setRefundResults] = useState<{ saleNumber: string; success: boolean; message: string }[]>([]);
  
  // Manuel iade için
  const [manualSaleNumber, setManualSaleNumber] = useState('');
  const [isManualProcessing, setIsManualProcessing] = useState(false);
  
  // ✅ YENİ: Sayfalama ve arama için
  const ITEMS_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  
  // ✅ Filtrelenmiş satışlar (arama sonuçları)
  const filteredSales = useMemo(() => {
    if (!searchQuery.trim()) return sales;
    
    const query = searchQuery.toLowerCase().trim();
    return sales.filter(sale => 
      sale.saleNumber.toLowerCase().includes(query) ||
      (sale.invoiceNo && sale.invoiceNo.toLowerCase().includes(query)) ||
      (sale.cardUID && sale.cardUID.toLowerCase().includes(query)) ||
      sale.amount.toString().includes(query)
    );
  }, [sales, searchQuery]);
  
  // ✅ Toplam sayfa sayısı
  const totalPages = Math.ceil(filteredSales.length / ITEMS_PER_PAGE);
  
  // ✅ Mevcut sayfadaki satışlar
  const paginatedSales = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredSales.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredSales, currentPage]);

  // Satışları yükle (sadece bugünün satışları)
  useEffect(() => {
    if (open && companyId) {
      const allSales = getPavoPaySales(companyId);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todaySales = allSales.filter(sale => {
        try {
          const saleDate = new Date(sale.date);
          saleDate.setHours(0, 0, 0, 0);
          return saleDate.getTime() === today.getTime();
        } catch {
          return false;
        }
      });
      setSales(todaySales);
      setSelectedSaleNumbers(new Set());
      setRefundResults([]);
      setManualSaleNumber('');
      setCurrentPage(1);
      setSearchQuery('');
      console.log('[PavoPayRefundModal] Bugünün satışları yüklendi:', todaySales.length, '/', allSales.length, 'toplam');
    }
  }, [open, companyId]);
  
  // ✅ Arama değiştiğinde sayfayı sıfırla
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Checkbox toggle
  const handleToggleSelect = (saleNumber: string) => {
    setSelectedSaleNumbers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(saleNumber)) {
        newSet.delete(saleNumber);
      } else {
        newSet.add(saleNumber);
      }
      return newSet;
    });
  };

  // Tümünü seç/kaldır (filtrelenmiş satışlara göre)
  const handleToggleAll = () => {
    const filteredSaleNumbers = filteredSales.map(s => s.saleNumber);
    const allSelected = filteredSaleNumbers.every(sn => selectedSaleNumbers.has(sn));
    
    if (allSelected) {
      // Filtrelenmiş satışları kaldır
      setSelectedSaleNumbers(prev => {
        const newSet = new Set(prev);
        filteredSaleNumbers.forEach(sn => newSet.delete(sn));
        return newSet;
      });
    } else {
      // Filtrelenmiş satışları ekle
      setSelectedSaleNumbers(prev => {
        const newSet = new Set(prev);
        filteredSaleNumbers.forEach(sn => newSet.add(sn));
        return newSet;
      });
    }
  };

  // Seçili satışları iptal et
  const handleCancelSelected = async () => {
    if (!companyId || selectedSaleNumbers.size === 0) return;

    setIsProcessing(true);
    setRefundResults([]);

    const selectedSales = Array.from(selectedSaleNumbers);
    const results: { saleNumber: string; success: boolean; message: string }[] = [];

    for (const saleNumber of selectedSales) {
      try {
        console.log('[PavoPayRefundModal] İptal ediliyor:', saleNumber);
        const saleRecord = sales.find(s => s.saleNumber === saleNumber);
        const result = await cancelPavoPaySale(saleNumber, companyId, saleRecord?.amount, createdBy);
        
        let message = result.success ? t('pavoPayRefundModal.messages.cancelSuccess') : (result.errorMessage || t('pavoPayRefundModal.messages.unknownError'));
        if (result.success && result.backendRefundSuccess) {
          message = t('pavoPayRefundModal.messages.cancelSuccessWithRefund');
        } else if (result.success && !result.backendRefundSuccess) {
          message = t('pavoPayRefundModal.messages.cancelSuccessRefundFailed');
        }
        
        results.push({
          saleNumber,
          success: result.success,
          message
        });
      } catch (error: any) {
        results.push({
          saleNumber,
          success: false,
          message: error.message || t('pavoPayRefundModal.messages.operationError')
        });
      }
    }

    setRefundResults(results);
    setIsProcessing(false);

    // Satışları yeniden yükle
    const updatedSales = getPavoPaySales(companyId);
    setSales(updatedSales);
    setSelectedSaleNumbers(new Set());

    // Sonuç özeti göster
    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    if (failCount === 0) {
      showAlertModal(t('pavoPayRefundModal.alerts.cancelSuccessTitle'), t('pavoPayRefundModal.alerts.cancelSuccessBody', { count: successCount }));
    } else if (successCount === 0) {
      showAlertModal(t('pavoPayRefundModal.alerts.cancelFailedTitle'), `${t('pavoPayRefundModal.alerts.cancelAllFailed')}\n\n${results.map(r => `${r.saleNumber}: ${r.message}`).join('\n')}`);
    } else {
      showAlertModal(t('pavoPayRefundModal.alerts.partialSuccessTitle'), `${t('pavoPayRefundModal.alerts.partialSuccessBody', { successCount, failCount })}\n\n${results.filter(r => !r.success).map(r => `${r.saleNumber}: ${r.message}`).join('\n')}`);
    }
  };

  const [manualAmount, setManualAmount] = useState('');

  const handleManualCancel = async () => {
    if (!companyId || !manualSaleNumber.trim()) {
      showAlertModal(t('pavoPayRefundModal.alerts.warningTitle'), t('pavoPayRefundModal.alerts.enterSaleNumber'));
      return;
    }

    const amount = manualAmount ? parseFloat(manualAmount) : undefined;

    setIsManualProcessing(true);

    try {
      console.log('[PavoPayRefundModal] Manuel iptal:', manualSaleNumber);
      const result = await cancelPavoPaySale(manualSaleNumber.trim(), companyId, amount, createdBy);

      if (result.success) {
        let msg = t('pavoPayRefundModal.alerts.manualCancelSuccessBody', { saleNumber: manualSaleNumber });
        if (result.backendRefundSuccess) {
          msg += `\n${t('pavoPayRefundModal.alerts.refundRecorded')}`;
        } else if (amount) {
          msg += `\n${t('pavoPayRefundModal.alerts.refundNotRecorded')}`;
        }
        showAlertModal(t('pavoPayRefundModal.alerts.cancelSuccessTitle'), msg);
        setManualSaleNumber('');
        setManualAmount('');
        
        const updatedSales = getPavoPaySales(companyId);
        setSales(updatedSales);
      } else {
        showAlertModal(t('pavoPayRefundModal.alerts.cancelFailedTitle'), `${t('pavoPayRefundModal.alerts.manualCancelFailed', { saleNumber: manualSaleNumber })}\n\n${t('pavoPayRefundModal.alerts.errorLabel')}: ${result.errorMessage}`);
      }
    } catch (error: any) {
      showAlertModal(t('pavoPayRefundModal.alerts.errorTitle'), `${t('pavoPayRefundModal.alerts.errorDuringOperation')}: ${error.message}`);
    } finally {
      setIsManualProcessing(false);
    }
  };

  // Tarihi formatla
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', py: 2 }}>
        {t('pavoPayRefundModal.title')}
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2, minHeight: 400 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label={t('pavoPayRefundModal.tabs.todaySales', { count: sales.length })} />
          <Tab label={t('pavoPayRefundModal.tabs.manualCancel')} />
        </Tabs>

        {/* TAB 1: Kayıtlı Satışlar */}
        <TabPanel value={tabValue} index={0}>
          {sales.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                {t('pavoPayRefundModal.emptyState.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('pavoPayRefundModal.emptyState.description')}
              </Typography>
            </Box>
          ) : (
            <>
              {/* ✅ YENİ: Arama Kutusu */}
              <TextField
                fullWidth
                size="small"
                placeholder={t('pavoPayRefundModal.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={isProcessing}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery 
                    ? t('pavoPayRefundModal.searchResultSummary', { query: searchQuery, count: filteredSales.length, page: currentPage, totalPages: totalPages || 1 })
                    : t('pavoPayRefundModal.todaySummary', { count: sales.length, page: currentPage, totalPages: totalPages || 1 })
                  }
                </Typography>
                <Button 
                  size="small" 
                  onClick={handleToggleAll}
                  variant="outlined"
                  disabled={isProcessing || filteredSales.length === 0}
                >
                  {filteredSales.every(s => selectedSaleNumbers.has(s.saleNumber)) && filteredSales.length > 0
                    ? t('pavoPayRefundModal.actions.clearAll')
                    : t('pavoPayRefundModal.actions.selectAll')}
                </Button>
              </Box>

              {/* ✅ Arama sonucu boş kontrolü */}
              {filteredSales.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('pavoPayRefundModal.noSearchResult', { query: searchQuery })}
                  </Typography>
                </Box>
              ) : (
                <>
                  <List sx={{ maxHeight: 350, overflow: 'auto' }}>
                    {paginatedSales.map((sale) => {
                  const isSelected = selectedSaleNumbers.has(sale.saleNumber);
                  const result = refundResults.find(r => r.saleNumber === sale.saleNumber);
                  
                  return (
                    <ListItem
                      key={sale.saleNumber}
                      sx={{
                        border: '1px solid #ddd',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: result 
                          ? (result.success ? '#e8f5e9' : '#ffebee')
                          : isSelected ? '#e3f2fd' : '#fff',
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(sale.saleNumber)}
                          disabled={isProcessing}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body1" fontWeight="bold">
                              {sale.saleNumber}
                            </Typography>
                            <Chip 
                              label={sale.paymentType} 
                              size="small" 
                              color={sale.paymentType === 'POS' ? 'primary' : sale.paymentType === 'CASH' ? 'success' : 'warning'}
                            />
                            <Typography variant="body2" color="success.main" fontWeight="bold">
                              {sale.amount.toFixed(2)} ₺
                            </Typography>
                            {sale.cardUID && (
                              <Chip
                                label={`Kart: ${sale.cardUID}`}
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                            )}
                            {result && (
                              <Chip 
                                label={result.success ? t('pavoPayRefundModal.status.cancelled') : t('pavoPayRefundModal.status.failed')}
                                size="small" 
                                color={result.success ? 'success' : 'error'}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(sale.date)} | {t('pavoPayRefundModal.receipt')}: {sale.invoiceNo || 'N/A'}
                            {sale.cardUID ? ` | Kart: ${sale.cardUID}` : ''}
                            {result && !result.success && (
                              <Typography component="span" variant="caption" color="error.main" sx={{ ml: 1 }}>
                                - {result.message}
                              </Typography>
                            )}
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
              
              {/* ✅ YENİ: Sayfalama */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination 
                    count={totalPages} 
                    page={currentPage} 
                    onChange={(_, page) => setCurrentPage(page)}
                    color="primary"
                    size="medium"
                    showFirstButton
                    showLastButton
                    disabled={isProcessing}
                  />
                </Box>
              )}
                </>
              )}
            </>
          )}
        </TabPanel>

        {/* TAB 2: Manuel İptal */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('pavoPayRefundModal.manual.description')}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('pavoPayRefundModal.manual.hint')}
            </Typography>

            <TextField
              fullWidth
              label={t('pavoPayRefundModal.manual.saleNumberLabel')}
              placeholder={t('pavoPayRefundModal.manual.saleNumberPlaceholder')}
              value={manualSaleNumber}
              onChange={(e) => setManualSaleNumber(e.target.value)}
              disabled={isManualProcessing}
              sx={{ mb: 2 }}
              InputProps={{
                sx: { fontSize: '1.2rem', fontWeight: 'bold' }
              }}
            />

            <TextField
              fullWidth
              label={t('pavoPayRefundModal.manual.refundAmountLabel')}
              placeholder={t('pavoPayRefundModal.manual.refundAmountPlaceholder')}
              type="number"
              value={manualAmount}
              onChange={(e) => setManualAmount(e.target.value)}
              disabled={isManualProcessing}
              sx={{ mb: 2 }}
              helperText={t('pavoPayRefundModal.manual.refundAmountHelper')}
            />

            <Button
              fullWidth
              variant="contained"
              color="error"
              size="large"
              onClick={handleManualCancel}
              disabled={isManualProcessing || !manualSaleNumber.trim()}
              sx={{ py: 2, fontSize: '1.1rem' }}
            >
              {isManualProcessing ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} />
                  {t('pavoPayRefundModal.actions.cancelling')}
                </>
              ) : (
                t('pavoPayRefundModal.actions.cancelSale')
              )}
            </Button>
          </Box>
        </TabPanel>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button 
          onClick={onClose}
          color="inherit"
          disabled={isProcessing || isManualProcessing}
        >
          {t('pavoPayRefundModal.actions.close')}
        </Button>
        
        {tabValue === 0 && sales.length > 0 && (
          <Button 
            onClick={handleCancelSelected}
            variant="contained"
            color="error"
            disabled={isProcessing || selectedSaleNumbers.size === 0}
            sx={{ minWidth: 200 }}
          >
            {isProcessing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                {t('pavoPayRefundModal.actions.processing')}
              </>
            ) : (
              t('pavoPayRefundModal.actions.cancelSelected', { count: selectedSaleNumbers.size })
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PavoPayRefundModal;
